import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Eye, EyeOff } from "lucide-react";

const BRAND_BLUE = "#0b679e";
const BRAND_DARK = "#084e78";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Email ou password incorretos");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div
        className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden"
        style={{ backgroundColor: BRAND_BLUE }}
      >
        <img
          src="/assets/bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10"
          onError={e => { e.target.style.display='none'; }}
        />
        <div className="relative z-10 text-center px-12">
          <img
            src="/assets/logo.png"
            alt="ACD Advogados"
            className="mx-auto mb-8 h-28 object-contain"
            onError={e => {
              e.target.style.display='none';
              document.getElementById('logo-fallback').style.display='block';
            }}
          />
          <div id="logo-fallback" style={{display:'none'}} className="mb-8">
            <h2 className="text-3xl font-bold text-white">ACD</h2>
            <p className="text-white/80 text-sm">Ana Cristina Domingues</p>
            <p className="text-white font-semibold">ADVOGADOS</p>
          </div>
          <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
            Portal de Gestão<br />Interno
          </h1>
          <p className="text-white/70 text-base">
            Plataforma interna de gestão de processos jurídicos
          </p>
          <div className="mt-10 flex items-center gap-6 justify-center text-white/50 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-white/60"></div>
              <span>Lisboa</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-white/60"></div>
              <span>Ponta Delgada</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex flex-1 lg:max-w-[480px] items-center justify-center bg-white px-8 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex justify-center mb-8 lg:hidden">
            <img src="/assets/logo.png" alt="ACD Advogados" className="h-16 object-contain"
              onError={e => { e.target.style.display='none'; }}/>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-1">Bem-vindo</h2>
          <p className="text-slate-500 text-sm mb-8">Inicie sessão para aceder ao portal</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="o.seu.email@acd-advogados.net"
                required
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none transition-all"
                style={{ "--tw-ring-color": BRAND_BLUE }}
                onFocus={e => e.target.style.borderColor = BRAND_BLUE}
                onBlur={e => e.target.style.borderColor = ''}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  data-testid="login-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg text-sm focus:outline-none transition-all"
                  onFocus={e => e.target.style.borderColor = BRAND_BLUE}
                  onBlur={e => e.target.style.borderColor = ''}
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full text-white font-semibold py-2.5 rounded-lg transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: loading ? BRAND_DARK : BRAND_BLUE }}
              onMouseEnter={e => !loading && (e.target.style.backgroundColor = BRAND_DARK)}
              onMouseLeave={e => !loading && (e.target.style.backgroundColor = BRAND_BLUE)}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>A entrar...</span>
                </>
              ) : "Entrar"}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} ACD Ana Cristina Domingues Advogados
          </p>
        </div>
      </div>
    </div>
  );
}
